import {
  Autocomplete, Paper, TextField, useTheme,
} from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import React from 'react';
import { AutocompleteProps } from '@mui/material/Autocomplete/Autocomplete';
import { QuestionOption } from '../models/Question';
import { menuPaperProps } from '../StyledFormInputs';

type QuestionComponentAutocompleteProps = {
  inputValue: QuestionOption | null;
  placeholderText: string;
  options?: QuestionOption[];
  onChange: (newValue: QuestionOption | null) => void;
  onBlur: ()=>void;
  calledFromOnboarding?: boolean;
  showAutoCompletePlaceholderText?: boolean;
} & Omit<AutocompleteProps<QuestionOption, false, false, false>, 'inputValue' | 'renderInput'>;

export const QuestionComponentAutocomplete = (props: QuestionComponentAutocompleteProps) => {
  const {
    onChange, options, inputValue, onBlur, placeholderText, calledFromOnboarding,
    showAutoCompletePlaceholderText,
    ...args
  } = props;

  const [open, setOpen] = React.useState(false);
  const theme = useTheme();

  const onOpen = () => {
    setOpen(true);
  };
  const onClose = () => {
    setOpen(false);
  };

  const sxHideClearButton = {
    '& div .MuiAutocomplete-popupIndicator': {
      display: 'none !important',
    },
  };

  const sxTextField = () => ({
    width: '100%',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  });

  return (
    <Autocomplete
      classes={{ ...props.classes }}
      sx={{
        ...sxHideClearButton,
      }}
      PaperComponent={({ children }) => (
        <Paper sx={menuPaperProps}>
          {children}
        </Paper>
      )}
      disablePortal
      onBlur={onBlur}
      options={options}
      value={props.inputValue}
      open={open}
      onOpen={onOpen}
      onClose={onClose}
      onChange={(event, newValue) => {
        props.onChange(newValue);
      }}
      getOptionLabel={(option: QuestionOption) => option.display}
      openOnFocus={false}
      renderInput={(params) => (
        <TextField
          variant="outlined"
          sx={[sxTextField, {
            ...(calledFromOnboarding ? {
              '& .MuiOutlinedInput-root': {
                borderRadius: theme.spacing(1),
                border: `1px solid ${blueGrey[200]}`,
                background: theme.palette.common.white,
                boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05), 0px 0px 0px 0px rgba(0, 0, 0, 0.00), 0px 0px 0px 0px rgba(0, 0, 0, 0.00)',
                paddingTop: theme.spacing(0),
                paddingBottom: theme.spacing(0),
                paddingLeft: theme.spacing(0),
                '&:hover': {
                  border: `1px solid ${blueGrey[200]}`,
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '&.Mui-focused': {
                  // this has to be important to override the default border
                  border: `1px solid ${blueGrey[200]} !important`,
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                },
                '& .MuiOutlinedInput-input': {
                  padding: `${theme.spacing(1.25)} ${theme.spacing(1.75)}`,
                  height: `${theme.spacing(4)}`,
                },
              },
            } : {}),
          }]}
          {...params}
          placeholder={(calledFromOnboarding && !showAutoCompletePlaceholderText) ?
            '' : placeholderText}
          id={placeholderText}
        />
      )}
      {...args}
    />
  );
};
